import React from 'react';
import { FeedInspectButton } from './FeedInspectActions';
import { FEED_CARD_SHELL, FEED_CHIP, FEED_INSPECT_BTN } from './investigation/feedCardStyles';
import {
  complianceSeverityLabel,
  complianceSeverityStyle,
  isComplianceHit,
  normalizeComplianceSeverity,
} from '../modules/Corporate/complianceSeverityStyles';

/**
 * Shared AML / OpenSanctions compliance alert card (Corporate + Person feeds).
 * CLEAN state renders a compact strip to preserve feed vertical space.
 *
 * @param {{ res: object, reactKey?: string }} props
 */
export default function ComplianceAlertCard({ res, reactKey }) {
  if (!res || res.ui_bucket !== 'compliance_alert') return null;

  const isFailedSanction =
    String(res.status).toUpperCase() === 'FAILED' || String(res.status).toUpperCase() === 'ERROR';
  const hasHit = isComplianceHit(res);
  const severity = normalizeComplianceSeverity(res.risk_level);
  const styles = complianceSeverityStyle(severity, { failed: isFailedSanction, dark: true });
  const isClean = !hasHit && !isFailedSanction;

  if (isClean) {
    return (
      <article
        key={reactKey}
        className="feed-result-card flex h-8 items-center justify-between gap-2 rounded-lg border border-emerald-500/25 bg-emerald-950/25 px-3 py-1 text-xs shadow-sm shadow-black/20 backdrop-blur-md"
        data-feed-result="true"
        data-sanctions-state="clean"
        data-testid="compliance-clean-banner"
        aria-label="Sanctions compliance clean"
      >
        <p className="min-w-0 flex-1 truncate font-semibold leading-none text-emerald-50/90">
          <span className="font-black uppercase tracking-wide text-emerald-300/90">Sanctions</span>
          <span className="text-emerald-700" aria-hidden>
            {' '}
            ·{' '}
          </span>
          <span className="text-emerald-200">CLEAN</span>
          {res.entity_checked ? (
            <>
              <span className="text-emerald-700" aria-hidden>
                {' '}
                ·{' '}
              </span>
              <span className="font-normal text-emerald-100/80">{res.entity_checked}</span>
            </>
          ) : (
            <span className="hidden font-normal text-emerald-100/70 sm:inline">
              {' '}
              · No international sanctions match
            </span>
          )}
        </p>
        <span className="shrink-0 rounded border border-emerald-400/30 bg-emerald-500/15 px-1.5 py-0.5 text-[8px] font-black uppercase tracking-wider text-emerald-200">
          CLEAN
        </span>
      </article>
    );
  }

  const headline = hasHit
    ? res.entity_checked
      ? `Entity: ${res.entity_checked}`
      : res.title || 'Sanctions Match Found'
    : 'Scan Failed / Unavailable';

  return (
    <article
      key={reactKey}
      className={`${FEED_CARD_SHELL} ${styles.shell}`.trim()}
      data-feed-result="true"
    >
      <div className="mb-4 flex items-start gap-4">
        <div
          className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl text-2xl shadow-inner ${styles.icon}`}
        >
          {isFailedSanction ? '🔌' : '⚠️'}
        </div>
        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h4 className={`text-[10px] font-black uppercase tracking-widest ${styles.label}`}>
              Sanctions Compliance
            </h4>
            <div className="flex flex-wrap items-center gap-1.5">
              <span className={`rounded-md px-2 py-0.5 text-[9px] font-black uppercase tracking-widest ${styles.badge}`}>
                {res.sanctions_check_badge ||
                  (hasHit
                    ? `SANCTIONS CHECK: ${String(res.risk_level || 'HIT').toUpperCase()}`
                    : 'SANCTIONS CHECK: UNAVAILABLE')}
              </span>
              {res.status && res.status !== 'completed' ? (
                <span className={`${FEED_CHIP} text-slate-300`}>Status: {res.status}</span>
              ) : null}
            </div>
          </div>
          <p className={`text-base font-black uppercase ${styles.body}`}>{headline}</p>
          {res.risk_level ? (
            <p className={`text-xs font-bold ${styles.risk}`}>
              RISK LEVEL: {complianceSeverityLabel(res.risk_level)}
            </p>
          ) : null}
        </div>
      </div>

      {hasHit && Array.isArray(res.alerts) && res.alerts.length > 0 ? (
        <div className="space-y-3 border-t border-white/5 pt-4">
          {res.alerts.map((alert, aIdx) => {
            if (!alert || typeof alert !== 'object') return null;
            const alertStyle = complianceSeverityStyle(alert?.severity || res?.risk_level, { dark: true });
            return (
              <div key={aIdx} className={`rounded-xl border p-4 ${alertStyle.nested}`}>
                <div className="mb-2 flex items-start justify-between gap-3">
                  <div className="min-w-0 pr-2">
                    <span className={`block text-sm font-bold uppercase leading-tight ${alertStyle.body}`}>
                      {alert?.matched_name || 'Match'}
                    </span>
                    {alert?.reason ? (
                      <span
                        className={`mt-1 block text-[10px] font-black uppercase tracking-widest ${alertStyle.label}`}
                      >
                        Reason: {alert.reason}
                      </span>
                    ) : null}
                  </div>
                  <div className="flex shrink-0 flex-col items-end">
                    <span className={`mb-1 rounded px-2 py-0.5 text-[10px] font-black ${alertStyle.badge}`}>
                      Score: {alert?.score ?? '—'}
                    </span>
                    {alert?.severity ? (
                      <span className={`text-[9px] font-black uppercase ${alertStyle.label}`}>
                        {alert.severity}
                      </span>
                    ) : null}
                    {alert?.type ? (
                      <span className={`text-[9px] font-bold uppercase ${alertStyle.muted}`}>
                        Type: {alert.type}
                      </span>
                    ) : null}
                  </div>
                </div>
                {Array.isArray(alert?.datasets) && alert.datasets.length > 0 ? (
                  <div className="mt-3 flex flex-wrap gap-1">
                    {alert.datasets.map((ds) => (
                      <span key={String(ds)} className={FEED_CHIP}>
                        {ds}
                      </span>
                    ))}
                  </div>
                ) : null}
                {alert?.url && alert.url !== '#' ? (
                  <FeedInspectButton
                    res={{
                      title: alert?.matched_name || 'Sanctions record',
                      snippet: alert?.reason,
                      url: alert.url,
                      platform: 'OpenSanctions',
                      details: alert,
                      alerts: [alert],
                    }}
                    label="Inspect Record"
                    className={`${FEED_INSPECT_BTN} mt-3`}
                  />
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}
    </article>
  );
}
